import { MasonryTrimKind } from "common/materials/masonryTypes";
import type { FullExteriorMaterials, ExteriorMaterial } from "./exteriorMaterialTypes";
import { masonrySlotPlusTrimKinds } from "./exteriorMaterialTypes";
import type { FullExteriorMasonry } from "./exteriorPaletteTypes";

export const wallExteriorMaterialSlotNames: readonly (keyof FullExteriorMaterials)[] = [
  "wallExterior0",
  "wallExterior1",
  "wallExterior2",
  "wallExterior3",
  "wallExterior4",
  "wallExterior5",
  "wallExterior6",
  "wallExterior7",
  "wallExterior8",
  "wallExterior9",
] as const;

export const trimExteriorMaterialSlotNames: readonly (keyof FullExteriorMaterials)[] = [
  "trimExterior0",
  "trimExterior1",
  "trimExterior2",
  "trimExterior3",
  "trimExterior4",
  "trimExterior5",
  "trimExterior6",
  "trimExterior7",
  "trimExterior8",
  "trimExterior9",
] as const;

export const roofMaterialSlotNames: readonly (keyof FullExteriorMaterials)[] = [
  "roof0",
  "roof1",
  "roof2",
  "roof3",
] as const;

export const masonryMaterialSlotNames: readonly (keyof FullExteriorMaterials)[] = [
  "masonry0",
  "masonry1",
  "masonry2",
  "masonry3",
] as const;

export const exteriorMaterialSlotNames: readonly (keyof FullExteriorMaterials)[] = [
  ...wallExteriorMaterialSlotNames,
  ...trimExteriorMaterialSlotNames,
  ...roofMaterialSlotNames,
  ...masonryMaterialSlotNames,
  "roofTrim0",
  "roofTrim1",
  "roofTrim2",
  "roofTrim3",
  "gutter0",
  "gutter1",
  "gutter2",
  "foundation0",
  "foundation1",
  "foundation2",
  "driveway0",
  "driveway1",
  "driveway2",
  "walks0",
  "walks1",
  "walks2",
  "porchCeiling0",
  "porchCeiling1",
  "porchCeiling2",
  "deck0",
  "deck1",
  "deck2",
  "deck3",
  "glass0",
  "glass1",
  "glass2",
  "glassTrim0",
  "glassTrim1",
  "glassTrim2",
  "doorExterior0",
  "doorExterior1",
  "doorExterior2",
  "doorExterior3",
  "garageExterior0",
  "garageExterior1",
  "garageExterior2",
  "garageExterior3",
  "hardwareExterior0",
  "hardwareExterior1",
  "hardwareExterior2",
  "propDefault0",
  "propDefault1",
  "propDefault2",
  "chimney0",
  "chimney1",
  "chimney2",
  "shutter0",
  "shutter1",
  "shutter2",
  "headCasing0",
  "headCasing1",
  "headCasing2",
  "headCasing3",
  "apronCasing0",
  "apronCasing1",
  "apronCasing2",
  "apronCasing3",
  "sideCasing0",
  "sideCasing1",
  "sideCasing2",
  "sideCasing3",
  "earth0",
  "road0",
  "grass0",
  "sidewalk0",
  "fence0",
  "retainingWall0",
  "curb0",
  "water0",
];

export const exteriorMasonrySlotNames: (keyof FullExteriorMasonry)[] = [
  "masonryTrim0",
  "masonryTrim1",
  "masonryTrim2",
  "masonryTrim3",
];
console.assert(
  masonrySlotPlusTrimKinds.length === exteriorMasonrySlotNames.length * Object.values(MasonryTrimKind).length,
);

export const exteriorMaterialChoices: ExteriorMaterial[] = [...exteriorMaterialSlotNames, ...masonrySlotPlusTrimKinds];

export const siteMaterialChoices: ExteriorMaterial[] = [...exteriorMaterialSlotNames, ...masonrySlotPlusTrimKinds];

export const exteriorCeilingMaterialChoices: ExteriorMaterial[] = [
  "porchCeiling0",
  "porchCeiling1",
  "porchCeiling2",
  ...wallExteriorMaterialSlotNames,
  ...trimExteriorMaterialSlotNames,
  "roof0",
  "roof1",
  "roof2",
  "roof3",
  "roofTrim0",
  "roofTrim1",
  "roofTrim2",
  "roofTrim3",
];

export const exteriorApronCasingMaterialChoices: ExteriorMaterial[] = [
  ...trimExteriorMaterialSlotNames,
  "apronCasing0",
  "apronCasing1",
  "apronCasing2",
  "apronCasing3",
  ...wallExteriorMaterialSlotNames,
  ...masonryMaterialSlotNames,
  ...masonrySlotPlusTrimKinds,
];

export const exteriorSideCasingMaterialChoices: ExteriorMaterial[] = [
  ...trimExteriorMaterialSlotNames,
  "sideCasing0",
  "sideCasing1",
  "sideCasing2",
  "sideCasing3",
  ...wallExteriorMaterialSlotNames,
  ...masonryMaterialSlotNames,
  ...masonrySlotPlusTrimKinds,
];

export const headCasingMaterialChoices: ExteriorMaterial[] = [
  ...wallExteriorMaterialSlotNames,
  ...trimExteriorMaterialSlotNames,
  ...masonryMaterialSlotNames,
  ...masonrySlotPlusTrimKinds,
  "headCasing0",
  "headCasing1",
  "headCasing2",
  "headCasing3",
];

export const mainWallMaterialChoices: ExteriorMaterial[] = [
  ...wallExteriorMaterialSlotNames,
  ...masonryMaterialSlotNames,
];

export const dormerWallMaterialChoices: ExteriorMaterial[] = [
  ...wallExteriorMaterialSlotNames,
  ...trimExteriorMaterialSlotNames,
  ...masonryMaterialSlotNames,
  ...masonrySlotPlusTrimKinds,
  "apronCasing0",
  "apronCasing1",
  "apronCasing2",
  "apronCasing3",
  "sideCasing0",
  "sideCasing1",
  "sideCasing2",
  "sideCasing3",
  "headCasing0",
  "headCasing1",
  "headCasing2",
  "headCasing3",
];

export const facadeMaterialChoices: ExteriorMaterial[] = [
  ...wallExteriorMaterialSlotNames,
  ...trimExteriorMaterialSlotNames,
  ...masonryMaterialSlotNames,
  ...masonrySlotPlusTrimKinds,
  "apronCasing0",
  "apronCasing1",
  "apronCasing2",
  "apronCasing3",
  "sideCasing0",
  "sideCasing1",
  "sideCasing2",
  "sideCasing3",
  "headCasing0",
  "headCasing1",
  "headCasing2",
  "headCasing3",
];

export const wainscotingMaterialChoices: ExteriorMaterial[] = [
  ...wallExteriorMaterialSlotNames,
  ...trimExteriorMaterialSlotNames,
  ...masonryMaterialSlotNames,
  ...masonrySlotPlusTrimKinds,
  "apronCasing0",
  "apronCasing1",
  "apronCasing2",
  "apronCasing3",
  "sideCasing0",
  "sideCasing1",
  "sideCasing2",
  "sideCasing3",
  "headCasing0",
  "headCasing1",
  "headCasing2",
  "headCasing3",
];

export const columnMaterialChoices: ExteriorMaterial[] = [
  ...wallExteriorMaterialSlotNames,
  ...trimExteriorMaterialSlotNames,
  ...masonryMaterialSlotNames,
  ...masonrySlotPlusTrimKinds,
  "apronCasing0",
  "apronCasing1",
  "apronCasing2",
  "apronCasing3",
  "sideCasing0",
  "sideCasing1",
  "sideCasing2",
  "sideCasing3",
  "headCasing0",
  "headCasing1",
  "headCasing2",
  "headCasing3",
];

export const roofTopMaterialChoices: ExteriorMaterial[] = ["roof0", "roof1", "roof2", "roof3", "trimExterior0"];

export const exteriorTrimMaterialChoices: ExteriorMaterial[] = [
  ...trimExteriorMaterialSlotNames,
  ...masonrySlotPlusTrimKinds,
];

export const exteriorRoofTrimMaterialChoices: ExteriorMaterial[] = [
  ...wallExteriorMaterialSlotNames,
  ...trimExteriorMaterialSlotNames,
  ...masonryMaterialSlotNames,
  ...masonrySlotPlusTrimKinds,
  "apronCasing0",
  "apronCasing1",
  "apronCasing2",
  "apronCasing3",
  "sideCasing0",
  "sideCasing1",
  "sideCasing2",
  "sideCasing3",
  "headCasing0",
  "headCasing1",
  "headCasing2",
  "headCasing3",
];

export const drivewayMaterialChoices: ExteriorMaterial[] = [
  "driveway0",
  "driveway1",
  "driveway2",
  "walks0",
  "walks1",
  "walks2",
  "foundation0",
];

export function isExteriorSlot(slot: string): slot is keyof FullExteriorMaterials {
  return (exteriorMaterialSlotNames as string[]).includes(slot);
}

export function isExteriorMasonrySlot(slot: string): slot is keyof FullExteriorMasonry {
  return (exteriorMasonrySlotNames as string[]).includes(slot);
}
